// Phase D6 (May 2026 — reversibility-first architecture).
//
// Universal Undo toast. Reads the per-user recent-actions buffer
// (KV-backed, 5-minute TTL) and surfaces the newest entry as a
// floating toast with a one-click "Undo" action. Clicking Undo
// posts to the D5 rollback-to-version endpoint with the entry's
// prior_version_id — restoring the entity to its pre-destructive
// state — then dismisses the entry from the buffer so it doesn't
// re-surface.
//
// Mounting
//   * Once in the authenticated app shell (App.tsx). Polls the
//     recent-actions endpoint every 30s.
//   * Hidden when the buffer is empty.
//   * Only the newest entry is shown at a time. Older entries
//     remain in the buffer (visible in the Recent actions tray
//     follow-up if/when that ships) but the toast doesn't stack.
//
// Behavior
//   * Toast auto-dismisses after 90s without action so a user
//     who navigates away doesn't return to a stale undo offer.
//   * Click Undo → call rollback endpoint → on success: dismiss
//     entry, show confirmation toast.
//   * Click "×" → dismiss entry without rolling back.

import { useCallback, useEffect, useState } from "react";

import { api, ApiError } from "../api";
import { useToast } from "./Toast";

interface RecentActionEntry {
  id: string;
  entity_type:
    | "contact"
    | "household"
    | "transaction"
    | "opportunity"
    | "service_record"
    | "rhythm_instance";
  entity_id: string;
  label: string;
  prior_version_id: string;
  occurred_at: string;
  action_class:
    | "merge"
    | "archive"
    | "delete"
    | "move"
    | "edit"
    | "rollback";
}

// Poll cadence for the recent-actions buffer. 60s (was 30s) — every
// open tab hits /api/recent-actions on this interval and each hit also
// drives a Better Auth session lookup against D1, so this is constant
// background DB load that scales with the team. Undo still appears
// instantly on the acting tab via the RECENT_ACTION_EVENT listener
// below; the poll only catches actions taken in OTHER tabs/sessions,
// where 60s latency is fine. Halving the poll rate halves that baseline
// D1 pressure (D1 perf, 2026-06-28).
const POLL_INTERVAL_MS = 60_000;
const AUTO_HIDE_AFTER_MS = 90_000;

const ACTION_CLASS_LABEL: Record<RecentActionEntry["action_class"], string> = {
  merge: "Merged",
  archive: "Archived",
  delete: "Deleted",
  move: "Moved",
  edit: "Edited",
  rollback: "Rolled back",
};

function formatRelative(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours} hr ago`;
}

// Fired by pages right after a destructive action succeeds so the
// toast shows immediately instead of waiting for the next poll.
const RECENT_ACTION_EVENT = "stewardship:recent-action";
export function announceRecentAction(): void {
  window.dispatchEvent(new Event(RECENT_ACTION_EVENT));
}

export function UndoToast() {
  const toast = useToast();
  const [entry, setEntry] = useState<RecentActionEntry | null>(null);
  const [running, setRunning] = useState(false);
  const [autoHidden, setAutoHidden] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const result = await api.listRecentActions();
      const newest = result.entries[0] ?? null;
      setEntry((current) => {
        // Only reset autoHidden when the newest entry's id changes.
        // Otherwise the toast keeps re-appearing every poll cycle.
        if (newest?.id !== current?.id) {
          setAutoHidden(false);
        }
        return newest;
      });
    } catch (err) {
      // 401 just means no session; silent.
      if (err instanceof ApiError && err.status === 401) return;
      // Other errors — log but don't toast (this is supplemental
      // info, not user-blocking).
      if (err instanceof Error) {
        console.warn("[undo-toast] poll failed", err.message);
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
    const handle = window.setInterval(() => {
      void refresh();
    }, POLL_INTERVAL_MS);
    // Audit B1: undo is feedback — it must appear the moment the
    // action lands, not on the next 30s poll. Pages that perform
    // destructive actions call announceRecentAction() after success.
    const onAnnounce = () => void refresh();
    window.addEventListener(RECENT_ACTION_EVENT, onAnnounce);
    return () => {
      window.clearInterval(handle);
      window.removeEventListener(RECENT_ACTION_EVENT, onAnnounce);
    };
  }, [refresh]);

  // Auto-hide stale entries after 90s.
  useEffect(() => {
    if (!entry || autoHidden) return;
    const ageMs = Date.now() - Date.parse(entry.occurred_at);
    const remaining = Math.max(0, AUTO_HIDE_AFTER_MS - ageMs);
    if (remaining === 0) {
      setAutoHidden(true);
      return;
    }
    const handle = window.setTimeout(() => setAutoHidden(true), remaining);
    return () => window.clearTimeout(handle);
  }, [entry, autoHidden]);

  const handleUndo = async () => {
    if (!entry || running) return;
    setRunning(true);
    try {
      // Audit B1: an archive's inverse is RESTORE — it needs no prior
      // version row, so it works for never-edited records too (the
      // rollback path below requires a version and can't cover them).
      if (entry.action_class === "archive") {
        if (entry.entity_type === "contact") {
          await api.restoreContact(entry.entity_id);
        } else if (entry.entity_type === "household") {
          await api.restoreHousehold(entry.entity_id);
        } else {
          throw new Error(
            `No restore path for archived ${entry.entity_type}`,
          );
        }
        await api.dismissRecentAction(entry.id);
        setEntry(null);
        toast.success(`Undid: ${entry.label} (restored).`);
        return;
      }
      const result = await api.rollbackEntityToVersion(
        entry.entity_type,
        entry.entity_id,
        {
          target_version_id: entry.prior_version_id,
          reason_code: "data_quality_cleanup",
          reason_text: `Undo from recent-actions toast: ${entry.label}`,
        },
      );
      if (result.success) {
        await api.dismissRecentAction(entry.id);
        setEntry(null);
        const fields =
          result.live_row_fields_applied === null
            ? "version chain recorded"
            : `${result.live_row_fields_applied} field${result.live_row_fields_applied === 1 ? "" : "s"} restored`;
        toast.success(`Undid: ${entry.label} (${fields}).`);
      } else {
        toast.error(`Undo failed: ${(result as { message?: string }).message ?? "unknown error"}`);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(`Undo failed: ${err.status} — ${err.message}`);
      } else {
        toast.error(
          `Undo failed: ${err instanceof Error ? err.message : "unknown"}`,
        );
      }
    } finally {
      setRunning(false);
    }
  };

  const handleDismiss = async () => {
    if (!entry || running) return;
    setRunning(true);
    try {
      await api.dismissRecentAction(entry.id);
      setEntry(null);
    } catch (err) {
      console.warn(
        "[undo-toast] dismiss failed",
        err instanceof Error ? err.message : err,
      );
    } finally {
      setRunning(false);
    }
  };

  if (!entry || autoHidden) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 9000,
        background: "var(--navy-surface)",
        color: "white",
        padding: "10px 14px",
        borderRadius: 8,
        boxShadow: "0 6px 20px rgba(0, 0, 0, 0.25)",
        display: "flex",
        alignItems: "center",
        gap: 12,
        fontSize: 13,
        maxWidth: "min(560px, calc(100vw - 32px))",
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <strong>{ACTION_CLASS_LABEL[entry.action_class]}:</strong>{" "}
        {entry.label}
        <span
          style={{
            marginLeft: 8,
            opacity: 0.7,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          · {formatRelative(entry.occurred_at)}
        </span>
      </span>
      <button
        type="button"
        onClick={() => void handleUndo()}
        disabled={running}
        style={{
          padding: "5px 12px",
          background: "var(--blue, #4e98c6)",
          color: "white",
          border: "none",
          borderRadius: 4,
          fontSize: 12,
          fontWeight: 600,
          cursor: running ? "wait" : "pointer",
          opacity: running ? 0.6 : 1,
          flexShrink: 0,
        }}
      >
        {running ? "Undoing…" : "Undo"}
      </button>
      <button
        type="button"
        onClick={() => void handleDismiss()}
        disabled={running}
        aria-label="Dismiss"
        title="Dismiss"
        style={{
          background: "transparent",
          color: "white",
          border: "none",
          fontSize: 18,
          lineHeight: 1,
          cursor: running ? "wait" : "pointer",
          opacity: 0.7,
          padding: "0 4px",
          flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}
